/**
 * Which kind of engagement a `modules_enabled` list describes.
 *
 * The three IV&V modules (Contract Compliance, Health Check, Go-Live
 * Readiness) share one overview and one AI proposal review queue. Discovery
 * and Selection do not: Discovery reviews its evidence on Discovery ->
 * Findings and Selection its suggestions on Selection -> Requirements. An
 * engagement with no IV&V module (City of Everett, 22 October 2026) was
 * still shown the IV&V overview and could still open the IV&V review queue,
 * both of which read like an IV&V project. The sidebar, the overview and
 * the review route all decide from these helpers so they agree.
 */

export const IVV_MODULES = ['contract_compliance', 'health_check', 'go_live_readiness'] as const;

export type IvvModule = (typeof IVV_MODULES)[number];

/** True when any IV&V module is on: the IV&V overview and review queue apply. */
export function hasIvvModule(modulesEnabled: ReadonlyArray<string>): boolean {
  return IVV_MODULES.some((m) => modulesEnabled.includes(m));
}

/**
 * Where the AI proposal review route sends an engagement that has no IV&V
 * module: Discovery's findings review when Discovery is on, else Selection.
 * `null` keeps the IV&V review queue (any IV&V module, or no module the
 * route could stand in for).
 */
export function reviewRedirectTarget(
  modulesEnabled: ReadonlyArray<string>,
): 'discovery' | 'selection' | null {
  if (hasIvvModule(modulesEnabled)) return null;
  if (modulesEnabled.includes('discovery')) return 'discovery';
  if (modulesEnabled.includes('selection')) return 'selection';
  return null;
}

/**
 * The service named in the hero's crumb ("<service> by Avero Advisors").
 * The prototype says "IV&V by"; a Discovery and Selection engagement is not
 * an IV&V one and says so.
 */
export function serviceLabel(modulesEnabled: ReadonlyArray<string>): string {
  if (hasIvvModule(modulesEnabled)) return 'IV&V';
  const discovery = modulesEnabled.includes('discovery');
  const selection = modulesEnabled.includes('selection');
  if (discovery && selection) return 'Discovery & Selection';
  if (discovery) return 'Discovery';
  if (selection) return 'Selection';
  return 'IV&V';
}
